import { mapActions, mapState, mapGetters } from 'vuex';
import { ToastProgrammatic as Toast } from 'buefy';
import dayjs from 'dayjs';
import _groupBy from 'lodash/groupBy';

import {
  GET_CALENDAR_INFO,
  GET_DASHBOARD_INFO,
  GET_NEWS,
} from '@/store/dashboard/constants';

import { GET_TEAM_INFO } from '@/store/setting/constants';
import {
  FRONT_STAGE_URL,
  REIMBURSEMENT_SITE_URL,
  USER_LEVEL,
} from '@/utils/constants';

export default {
  name: 'Dashboard',
  data() {
    return {
      loading: false,
      identification: '',
      info: {},
      calendar: [],
      selectedDate: null,
      selectedYear: dayjs().year(),
      selectedMonth: dayjs().month() + 1,
    };
  },
  computed: {
    ...mapState('dashboard', {
      gettingNews: ({ gettingNews }) => gettingNews,
      news: ({ news }) => news,
    }),
    ...mapGetters(['isProduction']),
    ...mapGetters('user', ['currentRole']),
    loginLink() {
      const domain = this.isProduction
        ? FRONT_STAGE_URL.PRODUCTION
        : FRONT_STAGE_URL.STAGING;
      return `${domain}/login?identification=${this.identification}`;
    },
    reimburseLink() {
      const domain = this.isProduction
        ? REIMBURSEMENT_SITE_URL.PRODUCTION
        : REIMBURSEMENT_SITE_URL.STAGING;
      return `${domain}/login?identification=${this.identification}`;
    },
    isHead() {
      return this.currentRole === USER_LEVEL.HEAD;
    },
    orderPreview() {
      return this.info.orders || [];
    },
    calendarEventsByDate() {
      return _groupBy(this.calendar, (ev) => dayjs(ev.start_time).format('YYYY-MM-DD'));
    },
    calendarEvents() {
      return Object.keys(this.calendarEventsByDate).map((date) => dayjs(date).toDate());
    },
    calendarEventInfo() {
      const target = dayjs(this.selectedDate).format('YYYY-MM-DD');

      return this.calendarEventsByDate[target] || [];
    },
    newsPlaceholder() {
      return this.gettingNews ? '正在載入最新消息⋯' : '還沒有最新消息喔！';
    },
  },
  async mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.initialize();
    try {
      await this.getNews();
    } catch (error) {
      Toast.open({
        message: '取得最新消息失敗。',
        type: 'is-danger',
      });
    }
    this.$nextTick(() => {
      const doneTutorial = localStorage?.getItem('done_tutorial') || 0;
      if (!+doneTutorial) {
        this.showTutorial();
      }
    });
  },
  methods: {
    async initialize() {
      try {
        this.loading = true;
        this.selectedDate = dayjs().toDate();
        this.info = await this.getDashboardInfo();
        const { identification } = await this.getTeamInfo();
        this.identification = identification;
        await this.updateCalendar();
      } catch (error) {
        const errorMessage = error?.message ? `(${error.message})` : '';

        Toast.open({
          message: `載入資訊失敗，請重新整理頁面。${errorMessage}`,
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    humanizeDate(date) {
      return dayjs(date).format('YYYY/MM/DD');
    },
    humanizeDateTime(date) {
      return dayjs(date).format('YYYY/MM/DD HH:mm');
    },
    onCopySuccess() {
      Toast.open({
        message: '已複製到剪貼簿',
        type: 'is-success',
      });
    },
    onCopyError() {
      Toast.open({
        message: '複製連結失敗，請手動複製連結',
        type: 'is-warning',
      });
    },
    onYearChange(year) {
      this.selectedYear = year;
      this.updateCalendar();
    },
    onMonthChange(month) {
      this.selectedMonth = month + 1;
      this.updateCalendar();
    },
    showTutorial() {
      const steps = [
        {
          title: '歡迎使用好彙訂，開始導覽囉',
          text: '歡迎您加入好彙訂，我們會介紹您各頁面的產品功能，一起來看看吧：）',
        },
        {
          target: '#sidebar-member-list',
          title: '訂購前置步驟1：加入人員',
          text: '此處可批次加入人員、賦予人員角色權限及刪除人員，為開始訂購前的必要步驟之一。',
        },
        {
          target: '#sidebar-store-list',
          title: '訂購前置步驟2：新增店家及菜單',
          text: '此處可新增、修改及刪除店家與菜單資訊，建立組織專屬的店家資料庫，為開始訂購前的必要步驟之一。',
        },
        {
          target: '#sidebar-order-list',
          title: '開始揪團：一起訂東西',
          text: '完善店家資訊及開通人員後，就可以來「一起訂東西」揪團囉！',
        },
      ];

      const tour = this.$shepherd({
        defaultStepOptions: {
          classes: 'shepherd',
          scrollTo: true,
        },
        exitOnEsc: true,
        useModalOverlay: true,
      });
      steps.forEach((step, index) => {
        // eslint-disable-next-line global-require, import/no-dynamic-require
        const src = require(`@/assets/images/tutorial-${index}.jpg`);
        const isFirstStep = index === 0;
        const isLastStep = index === steps.length - 1;

        tour.addStep({
          arrow: false,
          attachTo: isFirstStep ? undefined : { element: step.target || this.$el, on: 'right' },
          classes: isFirstStep ? '' : 'ml-4',
          cancelIcon: {
            enabled: true,
          },
          title: `
            <img alt="Tutorial Step ${index}" class="mb-4" src="${src}" />
            <label class="is-size-4 shepherd-header--title">${step.title}</label>
          `,
          text: step.text,
          buttons: [
            {
              classes: 'shepherd-footer--back-button mr-6',
              text: isFirstStep ? '略過導覽' : '上一步',
              action: isFirstStep ? tour.cancel : tour.back,
            },
            {
              classes: 'shepherd-footer--next-button',
              text: isLastStep ? '完成導覽' : '下一步',
              action: isLastStep ? tour.complete : tour.next,
            },
          ],
          when: {
            cancel() {
              localStorage.setItem('done_tutorial', 1);
            },
          },
        });
      });
      tour.on('cancel', () => {
        localStorage.setItem('done_tutorial', 1);
      });
      tour.on('complete', () => {
        localStorage.setItem('done_tutorial', 1);
      });

      tour.start();
    },
    async updateCalendar() {
      try {
        this.loading = true;
        this.calendar = await this.getCalendarInfo({
          year: this.selectedYear,
          month: this.selectedMonth,
        });
      } catch (ex) {
        Toast.open({
          message: '載入行事曆資訊失敗，請重試一次',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    ...mapActions('dashboard', {
      getCalendarInfo: GET_CALENDAR_INFO,
      getDashboardInfo: GET_DASHBOARD_INFO,
      getNews: GET_NEWS,
    }),
    ...mapActions('setting', {
      getTeamInfo: GET_TEAM_INFO,
    }),
  },
};
